import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth.jsx';
import { useSettingsMutations } from '../../../hooks/useSettings.js';
import { ApiError } from '../../../lib/api.js';

/**
 * DangerZonePage — destructive actions, intentionally isolated.
 *
 * Currently: delete organisation + account.
 *
 * Delete flow is multi-step by design:
 *   1. Click "Delete organisation" button
 *   2. Modal-style card appears inline asking for confirmation phrase
 *   3. User types the tenant name verbatim AND their current password
 *   4. Backend cascades: delete tenant, staff, scans, R2 objects,
 *      cancel Stripe subscription, delete admins
 *   5. User is logged out + redirected to signup
 *
 * Friction is the feature — accidental account deletion is worse
 * than a slightly annoying confirmation flow.
 */

export function DangerZonePage() {
  const { tenant, admin, refresh: refreshAuth } = useAuth();
  const { deleteAccount } = useSettingsMutations();
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const expectedConfirmation = tenant?.name || '';
  const confirmationMatches = confirmationText === expectedConfirmation;

  async function handleDelete() {
    setError(null);
    if (!confirmationMatches) {
      setError(`Please type "${expectedConfirmation}" exactly to confirm.`);
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setDeleting(true);
    try {
      await deleteAccount(password, confirmationText);
      // Backend has deleted everything. Log out locally + redirect.
      await refreshAuth?.();
      navigate('/signup', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete account.');
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="settings-card" style={{ marginBottom: 'var(--sp-5)' }}>
        <h2 className="settings-card-title">Danger zone</h2>
        <p className="settings-card-body">
          Actions here are permanent. Please read carefully before
          proceeding.
        </p>
      </div>

      <div className="danger-card">
        <h3 className="danger-card-title">Delete organisation</h3>
        <p className="danger-card-body">
          Permanently delete <strong>{tenant?.name || 'your organisation'}</strong>,
          all staff records, all scan history, all branding, all uploaded
          photos, and all team admins. Any active Stripe subscription is
          cancelled.{' '}
          <br /><br />
          <strong>This cannot be undone.</strong> Printed QR codes stop
          working immediately.
        </p>

        {!confirmOpen ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setConfirmOpen(true)}
            >
              Delete organisation
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 'var(--sp-4)', padding: 'var(--sp-4)', background: 'var(--paper)', border: '1px solid rgba(230, 57, 70, 0.3)', borderRadius: 'var(--radius)' }}>
            <p style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>
              To confirm, type <code style={{ fontFamily: 'var(--font-mono)', background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--line)' }}>
                {expectedConfirmation}
              </code> below and enter your password.
            </p>

            {error && <div className="alert alert-error" role="alert">{error}</div>}

            <div className="field">
              <label className="field-label" htmlFor="f-delete-confirm">Organisation name</label>
              <input
                id="f-delete-confirm"
                className="field-input"
                type="text"
                value={confirmationText}
                onChange={e => setConfirmationText(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)' }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="f-delete-password">Your password</label>
              <input
                id="f-delete-password"
                className="field-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmationText('');
                  setPassword('');
                  setError(null);
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || !confirmationMatches || !password}
                aria-busy={deleting}
              >
                {deleting ? (
                  <><span className="spinner" aria-hidden="true" /> Deleting…</>
                ) : (
                  'Permanently delete'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="muted" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center', marginTop: 'var(--sp-5)' }}>
        Logged in as {admin?.email}
      </p>
    </>
  );
}
